import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useOrg, OrgRole } from "@/hooks/useOrg";
import { Shield } from "lucide-react";

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles: Array<"admin" | "operator" | "viewer">;
}

// Maps legacy app roles to org roles
const ROLE_MAP: Record<string, OrgRole[]> = {
  admin: ["owner", "admin"],
  operator: ["owner", "admin", "operator"],
  viewer: ["owner", "admin", "operator", "viewer"],
};

const RoleProtectedRoute = ({ children, requiredRoles }: RoleProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { role, loading: orgLoading, needsSetup } = useOrg();

  if (authLoading || orgLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (needsSetup) {
    return <Navigate to="/setup-org" replace />;
  }

  // Check if user's org role satisfies any of the required app roles
  const hasAccess = role && requiredRoles.some((appRole) => {
    const allowedOrgRoles = ROLE_MAP[appRole] || [];
    return allowedOrgRoles.includes(role);
  });

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-3">
          <Shield className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="text-lg font-semibold">Access Denied</h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            You don't have the required permissions to access this page.
            Contact your organization administrator for access.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;
